import { Badge, Flex, SimpleGrid, Text, TextInput } from '@mantine/core'
import { Button } from 'components/Button'
import { useTranslation } from 'react-i18next'
import type { PersonalInfoSectionProps } from '../types'
import { AvatarUpload } from './AvatarUpload'

/** Maps backend profile names to badge colors */
const ROLE_COLORS: Record<string, string> = {
  student: 'green',
  tutor: 'blue',
  manager: 'violet',
  guardian: 'orange',
}

/**
 * Displays and allows editing of the user's personal information.
 * Includes avatar upload, name, email, username, and role badge.
 */
export function PersonalInfoSection({
  firstName, lastName, email, username, currentProfile,
  avatarUrl, uploading, saving, isEmailEditable,
  errors, onFieldChange, onFileSelect, onBase64Capture, onRemoveAvatar, onSave,
}: PersonalInfoSectionProps) {
  const { t } = useTranslation()
  const roleColor = ROLE_COLORS[currentProfile] || 'gray'
  const roleLabel = t(`my_profile.role_${currentProfile}`, currentProfile)

  return (
    <div>
      <Text fw={600} size='lg' mb='md'>
        {t('my_profile.personal_info')}
      </Text>

      <AvatarUpload
        avatarUrl={avatarUrl}
        firstName={firstName}
        lastName={lastName}
        uploading={uploading}
        onFileSelect={onFileSelect}
        onBase64Capture={onBase64Capture}
        onRemoveAvatar={onRemoveAvatar}
      />

      <SimpleGrid cols={2} mt='md' breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
        <TextInput
          size='md'
          label={t('my_profile.first_name')}
          value={firstName}
          onChange={e => onFieldChange('firstName', e.currentTarget.value)}
          error={errors.firstName}
          required
        />
        <TextInput
          size='md'
          label={t('my_profile.last_name')}
          value={lastName}
          onChange={e => onFieldChange('lastName', e.currentTarget.value)}
          error={errors.lastName}
        />
      </SimpleGrid>

      <SimpleGrid cols={2} mt='md' breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
        <TextInput
          size='md'
          label={t('my_profile.email')}
          value={email}
          onChange={e => onFieldChange('email', e.currentTarget.value)}
          error={errors.email}
          disabled={!isEmailEditable}
        />
        <TextInput
          size='md'
          label={t('my_profile.username')}
          value={username}
          onChange={e => onFieldChange('username', e.currentTarget.value)}
          error={errors.username}
        />
      </SimpleGrid>

      <Text size='sm' fw={500} mt='md' mb={4}>
        {t('my_profile.role')}
      </Text>
      <Badge size='lg' color={roleColor} variant='light'>
        {roleLabel}
      </Badge>

      <Flex justify='flex-end' mt='sm'>
        <Button variant='filled' color='turquoise' onClick={onSave} loading={saving}>
          {t('my_profile.save')}
        </Button>
      </Flex>
    </div>
  )
}
